import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Eye, Link as LinkIcon, Loader, Upload, Globe } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VideosTab = ({ videos, categories, onRefresh }) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [newVideo, setNewVideo] = useState({ url: '', category: '' });
  const [bulkImport, setBulkImport] = useState({ source_url: '', max_videos: 20, category: 'auto-detect' });
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  const handleAddVideo = async (e) => {
    e.preventDefault();
    if (!newVideo.url.trim()) {
      toast.error('Please enter a valid video URL');
      return;
    }

    setLoading(true);
    setExtracting(true);
    
    try {
      const response = await axios.post(`${API}/admin/videos`, {
        url: newVideo.url.trim(),
        category: newVideo.category || null
      });
      
      toast.success('Video added successfully! Metadata extracted automatically.');
      setNewVideo({ url: '', category: '' });
      setShowAddDialog(false);
      onRefresh();
    } catch (error) {
      console.error('Error adding video:', error);
      toast.error(error.response?.data?.detail || 'Failed to add video');
    } finally {
      setLoading(false);
      setExtracting(false);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/videos/${videoId}`);
      toast.success('Video deleted successfully');
      onRefresh();
    } catch (error) {
      console.error('Error deleting video:', error);
      toast.error('Failed to delete video');
    }
  };

  const handleBulkImport = async (e) => {
    e.preventDefault();
    if (!bulkImport.source_url.trim()) {
      toast.error('Please enter a source URL');
      return;
    }

    setBulkImporting(true);
    setImportResults(null);
    
    try {
      const response = await axios.post(`${API}/admin/videos/bulk-import`, {
        source_url: bulkImport.source_url.trim(),
        max_videos: parseInt(bulkImport.max_videos),
        category: bulkImport.category || null
      });
      
      setImportResults(response.data);
      toast.success(`Bulk import completed! Imported: ${response.data.imported}, Failed: ${response.data.failed}`);
      
      if (response.data.imported > 0) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error bulk importing videos:', error);
      toast.error(error.response?.data?.detail || 'Failed to bulk import videos');
    } finally {
      setBulkImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-brand font-bold text-white">Video Management</h2>
        
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <div className="flex space-x-3">
              <Button className="btn-hover bg-gradient-to-r from-blue-600 to-purple-600 text-white" data-testid="add-video-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Video
              </Button>
              <Button 
                className="btn-hover bg-gradient-to-r from-green-600 to-cyan-600 text-white" 
                onClick={() => setShowBulkImportDialog(true)}
                data-testid="bulk-import-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                Bulk Import
              </Button>
            </div>
          </DialogTrigger>
          
          <DialogContent className="glass-dark border-white/20 text-white">
            <DialogHeader>
              <DialogTitle className="text-white">Add New Video</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleAddVideo} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="video-url" className="text-white">Video URL</Label>
                <div className="relative">
                  <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/50" />
                  <Input
                    id="video-url"
                    type="url"
                    value={newVideo.url}
                    onChange={(e) => setNewVideo(prev => ({ ...prev, url: e.target.value }))}
                    placeholder="https://example.com/video-url"
                    className="pl-10 form-input text-white"
                    required
                    data-testid="video-url-input"
                  />
                </div>
                <p className="text-xs text-white/60">
                  Supported sites: xhamster, pornhub, xvideos, and other popular adult sites
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="video-category" className="text-white">Category (Optional)</Label>
                <Select value={newVideo.category} onValueChange={(value) => setNewVideo(prev => ({ ...prev, category: value }))}>
                  <SelectTrigger className="form-input text-white">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-white/20">
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.name} className="text-white hover:bg-white/10">
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {extracting && (
                <Alert className="border-blue-500/50 bg-blue-500/10">
                  <Loader className="h-4 w-4 animate-spin" />
                  <AlertDescription className="text-blue-400">
                    Extracting video metadata (title, description, thumbnail)...
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="flex space-x-3">
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 btn-hover bg-gradient-to-r from-green-600 to-blue-600 text-white"
                  data-testid="submit-video"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="loading-spinner mr-2"></div>
                      Adding...
                    </div>
                  ) : (
                    'Add Video'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddDialog(false)}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        
        {/* Bulk Import Dialog */}
        <Dialog open={showBulkImportDialog} onOpenChange={setShowBulkImportDialog}>
          <DialogContent className="glass-dark border-white/20 text-white max-w-3xl">
            <DialogHeader>
              <DialogTitle className="text-white">Bulk Import Videos</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleBulkImport} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="source-url" className="text-white">Source Website URL</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/50" />
                  <Input
                    id="source-url"
                    type="url"
                    value={bulkImport.source_url}
                    onChange={(e) => setBulkImport(prev => ({ ...prev, source_url: e.target.value }))}
                    placeholder="https://example-adult-site.com"
                    className="pl-10 form-input text-white"
                    required
                    data-testid="bulk-source-url"
                  />
                </div>
                <p className="text-xs text-white/60">
                  Enter the URL of an adult video website to scrape video links from
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="max-videos" className="text-white">Maximum Videos</Label>
                  <Input
                    id="max-videos"
                    type="number"
                    value={bulkImport.max_videos}
                    onChange={(e) => setBulkImport(prev => ({ ...prev, max_videos: e.target.value }))}
                    min="1"
                    max="100"
                    className="form-input text-white"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="bulk-category" className="text-white">Category (Optional)</Label>
                  <Select value={bulkImport.category} onValueChange={(value) => setBulkImport(prev => ({ ...prev, category: value }))}>
                    <SelectTrigger className="form-input text-white">
                      <SelectValue placeholder="Auto-detect or select category" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-white/20">
                      <SelectItem value="auto-detect" className="text-white hover:bg-white/10">
                        Auto-detect from page
                      </SelectItem>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.name} className="text-white hover:bg-white/10">
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {bulkImporting && (
                <Alert className="border-blue-500/50 bg-blue-500/10">
                  <Loader className="h-4 w-4 animate-spin" />
                  <AlertDescription className="text-blue-400">
                    Scraping video links and extracting metadata... This may take a few minutes.
                  </AlertDescription>
                </Alert>
              )}
              
              {importResults && (
                <Alert className={`border-${importResults.imported > 0 ? 'green' : 'yellow'}-500/50 bg-${importResults.imported > 0 ? 'green' : 'yellow'}-500/10`}>
                  <AlertDescription className={`text-${importResults.imported > 0 ? 'green' : 'yellow'}-400`}>
                    <div><strong>Import Complete!</strong></div>
                    <div>✅ Imported: {importResults.imported} videos</div>
                    <div>❌ Failed: {importResults.failed} videos</div>
                    <div>🔍 Total links found: {importResults.total_links_found}</div>
                    {importResults.failed_urls && importResults.failed_urls.length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-xs">View failed URLs</summary>
                        <div className="mt-1 text-xs max-h-20 overflow-y-auto">
                          {importResults.failed_urls.map((url, i) => (
                            <div key={i} className="truncate">{url}</div>
                          ))}
                        </div>
                      </details>
                    )}
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="flex space-x-3">
                <Button
                  type="submit"
                  disabled={bulkImporting}
                  className="flex-1 btn-hover bg-gradient-to-r from-green-600 to-cyan-600 text-white"
                  data-testid="submit-bulk-import"
                >
                  {bulkImporting ? (
                    <div className="flex items-center">
                      <div className="loading-spinner mr-2"></div>
                      Importing...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Upload className="h-4 w-4 mr-2" />
                      Start Bulk Import
                    </div>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowBulkImportDialog(false);
                    setImportResults(null);
                  }}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Close
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      
      {/* Videos List */}
      <div className="space-y-4">
        {videos.length === 0 ? (
          <Card className="glass-dark border-white/20">
            <CardContent className="p-12 text-center">
              <div className="text-6xl mb-4">📹</div>
              <h3 className="text-xl font-semibold mb-2 text-white">No Videos Yet</h3>
              <p className="text-white/70 mb-4">Start adding videos to build your content library.</p>
              <Button
                onClick={() => setShowAddDialog(true)}
                className="btn-hover bg-gradient-to-r from-blue-600 to-purple-600 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Video
              </Button>
            </CardContent>
          </Card>
        ) : (
          videos.map((video) => (
            <VideoItem
              key={video.id}
              video={video}
              onDelete={() => handleDeleteVideo(video.id)}
            />
          ))
        )}
      </div>
    </div>
  );
};

const VideoItem = ({ video, onDelete }) => {
  return (
    <Card className="glass-dark border-white/20 card-hover">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0 mr-6">
            <div className="flex items-start space-x-4">
              {/* Thumbnail */}
              <div className="w-32 h-20 bg-gradient-to-br from-purple-900/50 to-pink-900/50 rounded-lg overflow-hidden flex-shrink-0">
                {video.thumbnail ? (
                  <img 
                    src={video.thumbnail} 
                    alt={video.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className="w-full h-full flex items-center justify-center text-white/50">
                  <LinkIcon className="h-6 w-6" />
                </div>
              </div>
              
              {/* Video Info */}
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-white mb-2 truncate">
                  {video.title}
                </h3>
                <p className="text-sm text-white/70 mb-3 line-clamp-2">
                  {video.description}
                </p>
                
                <div className="flex flex-wrap items-center gap-3 text-xs text-white/60">
                  <span className="flex items-center">
                    <Eye className="h-3 w-3 mr-1" />
                    {video.views.toLocaleString()} views
                  </span>
                  {video.duration && (
                    <Badge variant="outline" className="border-purple-500/30 text-purple-300">
                      {video.duration}
                    </Badge>
                  )}
                  {video.category && (
                    <Badge variant="secondary" className="bg-blue-500/20 text-blue-300">
                      {video.category}
                    </Badge>
                  )}
                </div>
                
                <div className="mt-2">
                  <a 
                    href={video.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-xs text-purple-400 hover:text-purple-300 truncate block"
                  >
                    {video.url}
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open(`/video/${video.id}`, '_blank')}
              className="text-white/70 hover:text-white hover:bg-white/10"
              data-testid={`view-video-${video.id}`}
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              data-testid={`delete-video-${video.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default VideosTab;